import { useState } from "react";
import { Link } from "@/components/Link";
import type { Store } from "@/api/storeService";
import { StoreBranchesQuickGlance } from "@/components/StoreBranchesCard";
import { stripBidiControls } from "@/lib/seo";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { storeEventProps } from "@/lib/posthog";
import { useClampOverflow } from "@/hooks/useClampOverflow";

type AboutStoreProps = {
  store: Store;
  isEditMode?: boolean;
  onUpdateField?: (field: string, value: unknown) => void;
  editedStore?: Partial<Store>;
};

// ─── Quick Details sidebar ────────────────────────────────────────────────────

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        padding: "8px 0",
        borderBottom: "1px solid var(--ys-rule)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--ys-font)",
          fontWeight: 500,
          fontSize: 12,
          color: "var(--ys-ink-3)",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--ys-font)",
          fontSize: 13,
          color: "var(--ys-ink)",
          textAlign: "start",
          lineHeight: 1.5,
        }}
      >
        {children}
      </span>
    </div>
  );
}

// quick-260813-ttw: known payment enum values (the merchant editor's checkbox set) map to
// Arabic; unknown strings pass through untouched — scraped metadata is free-form.
const PAYMENT_LABELS: Record<string, string> = {
  instapay: "إنستاباي",
  cod: "الدفع عند الاستلام",
  visa: "فيزا / بطاقة",
};

const toPaymentLabel = (value: string): string => PAYMENT_LABELS[value] ?? value;

function QuickDetails({ store }: { store: Store }) {
  const meta = (store.metadata ?? {}) as Record<string, unknown>;
  const { trackStore } = usePostHogTracking();
  const storeTrackingProps = storeEventProps(store);

  // Prefer the PRIMARY branch's address over `googlePlaces.address`. `googlePlaces` is only a
  // mirror of the primary branch, and merchant branch edits do not re-mirror it, so it goes stale
  // the moment a merchant corrects their own address. Reading `branches` directly makes that
  // staleness irrelevant to what visitors see.
  const primaryBranch =
    (Array.isArray(store.branches) ? store.branches : []).find((b) => b?.isPrimary) ??
    (Array.isArray(store.branches) ? store.branches[0] : undefined);
  const address =
    primaryBranch?.address || store.googlePlaces?.address || store.location;
  const geo = primaryBranch?.geometry || store.googlePlaces?.geometry;

  // quick-260813-ttw precedence: merchant-set top-level Store fields WIN over scraped metadata.
  // Merchant edits to the curated instant-tier fields land on the top-level keys, so reading
  // only `metadata` here would silently hide every merchant save.
  const deliveryRaw =
    (Array.isArray(store.deliveryGovernorates) && store.deliveryGovernorates.length > 0
      ? store.deliveryGovernorates
      : undefined) ??
    (meta.deliveryGovernorates as string[] | string | undefined) ??
    (meta.delivery as string | undefined);
  const delivery = Array.isArray(deliveryRaw)
    ? deliveryRaw.join(" · ")
    : deliveryRaw;

  const paymentRaw =
    (Array.isArray(store.paymentOptions) && store.paymentOptions.length > 0
      ? store.paymentOptions
      : undefined) ??
    (meta.paymentOptions as string[] | string | undefined) ??
    (meta.payment as string | undefined);
  const payment = Array.isArray(paymentRaw)
    ? paymentRaw.map(toPaymentLabel).join(" · ")
    : paymentRaw;

  const workingHours =
    store.workingHours || (meta.workingHours as string | undefined);
  const returnPolicy =
    store.refundPolicy ??
    (meta.returnPolicy as string | undefined) ??
    (meta.refund_policy as string | undefined);
  const languages = meta.languages as string | undefined;

  // quick-260813-ttw: merchant custom fields — approval-gated server-side, so this array only
  // ever contains published values and is always safe to render.
  const customFields = Array.isArray(store.customFields) ? store.customFields : [];

  // BRNCH-01: branches live here as a single count row, not as a full-width card — the list
  // itself opens in a sheet. Counted in hasAnyData so a branches-only store still gets the panel.
  const branchList = Array.isArray(store.branches) ? store.branches : [];
  const branchCount = branchList.length;

  // BRNCH-SEO: the sheet's contents never reach the server HTML (Radix portals don't render
  // during SSR), so the one branch fact worth putting in the rendered page is WHERE the branches
  // are — real visible text a user reads, not a hidden duplicate for crawlers. Governorates
  // rather than branch names: the stored names are Latin-script and noisy
  // ("crystal teeth of italy -el mokattam- dr mostafa mahmoud") and add no Arabic query terms.
  // stripBidiControls is required — governorates are stored as "القاهرة‬", so a raw Set
  // would treat a later clean "القاهرة" as a second distinct value.
  const branchGovernorates = Array.from(
    new Set(
      branchList.map((branch) => stripBidiControls(branch?.governorate || "")).filter(Boolean),
    ),
  );
  const shownGovernorates = branchGovernorates.slice(0, 5);
  const hiddenGovernorateCount = branchGovernorates.length - shownGovernorates.length;

  const hasAnyData =
    address ||
    delivery ||
    payment ||
    workingHours ||
    returnPolicy ||
    languages ||
    customFields.length > 0 ||
    branchCount > 0;

  if (!hasAnyData) return null;

  return (
    <div dir="rtl">
      <div
        style={{
          background: "var(--ys-paper)",
          borderRadius: "var(--ys-r-md)",
          boxShadow: "var(--ys-shadow-1)",
          border: "1px solid var(--ys-rule)",
          padding: 18,
          direction: "rtl",
        }}
      >
      <h3
        style={{
          fontFamily: "var(--ys-font)",
          fontWeight: 600,
          fontSize: 15,
          color: "var(--ys-ink)",
          margin: "0 0 10px 0",
        }}
      >
        تفاصيل سريعة
      </h3>
      <div
        style={{
          height: 1,
          background: "var(--ys-rule)",
          marginBottom: 4,
        }}
      />

      {address && (
        <DetailRow label="العنوان">
          {geo ? (
            <a
              href={`https://maps.google.com/?q=${geo.lat},${geo.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackStore.contactClicked({
                  platform: "google_maps",
                  surface: "about_card",
                  ...storeTrackingProps,
                })
              }
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "block",
              }}
            >
              {address}
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "var(--ys-primary)",
                  marginTop: 2,
                }}
              >
                خرائط جوجل ↗
              </span>
            </a>
          ) : (
            address
          )}
        </DetailRow>
      )}

      {branchCount > 0 && (
        <DetailRow label="الفروع">
          <StoreBranchesQuickGlance
            branches={store.branches}
            trackingProps={storeTrackingProps}
          />
          {shownGovernorates.length > 0 && (
            <span
              style={{
                display: "block",
                fontSize: 11,
                color: "var(--ys-ink-3)",
                marginTop: 2,
              }}
            >
              {shownGovernorates.join("، ")}
              {hiddenGovernorateCount > 0 && " وأكثر"}
            </span>
          )}
        </DetailRow>
      )}

      {delivery && <DetailRow label="التوصيل">{delivery}</DetailRow>}
      {payment && <DetailRow label="الدفع">{payment}</DetailRow>}
      {workingHours && <DetailRow label="ساعات العمل">{workingHours}</DetailRow>}
      {returnPolicy && <DetailRow label="سياسة الإرجاع">{returnPolicy}</DetailRow>}
      {languages && <DetailRow label="اللغات">{languages}</DetailRow>}

      {/* quick-260813-ttw: merchant custom fields render AFTER the fixed rows. Values are React
          text children (auto-escaped) — never HTML. */}
      {customFields.map((field) => (
        <DetailRow key={field.key} label={field.label}>
          {field.value}
        </DetailRow>
      ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AboutStore({ store, isEditMode }: AboutStoreProps) {
  // Mobile clamp toggle for the description — deterministic initial value (SSR-safe).
  const [descExpanded, setDescExpanded] = useState(false);
  // Measured overflow: the button only appears when the clamp actually hides content.
  const { ref: descRef, isTruncated } = useClampOverflow<HTMLParagraphElement>(
    descExpanded,
    store.description
  );
  const prominentCatId = store.prominentCategory?.categoryId;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 32,
        direction: "rtl",
      }}
      className="about-store-grid"
    >
      {/* Left: description + categories + tags */}
      <div>
        <h2
          style={{
            fontFamily: "var(--ys-font)",
            fontWeight: 700,
            fontSize: 22,
            color: "var(--ys-ink)",
            margin: "0 0 14px 0",
          }}
        >
          عن المتجر
        </h2>

        {store.description ? (
          <>
            {/* CSS clamp only on mobile — full description stays in SSR HTML for SEO */}
            <p
              ref={descRef}
              className={descExpanded ? undefined : "line-clamp-5 lg:line-clamp-none"}
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 15,
                color: "var(--ys-ink-2)",
                lineHeight: 1.6,
                margin: "0 0 16px 0",
              }}
            >
              {store.description}
            </p>
            {isTruncated && (
              <button
                type="button"
                onClick={() => setDescExpanded((v) => !v)}
                className="lg:hidden"
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ys-primary)",
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  margin: "-8px 0 16px 0",
                  cursor: "pointer",
                  display: "block",
                }}
              >
                {descExpanded ? "عرض أقل" : "عرض المزيد"}
              </button>
            )}
          </>
        ) : (
          !isEditMode && (
            <p
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 14,
                color: "var(--ys-ink-3)",
                fontStyle: "italic",
                marginBottom: 16,
              }}
            >
              لا يوجد وصف متاح حالياً.
            </p>
          )
        )}

        {/* Category chips */}
        {store.categories && store.categories.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {store.categories.map((cat, i) => {
              const isProminent = cat.categoryId === prominentCatId;
              return (
                <Link
                  key={i}
                  to={`/category/${cat.category.slug}`}
                  style={{ textDecoration: "none" }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontFamily: "var(--ys-font)",
                      fontWeight: 500,
                      fontSize: 12,
                      borderRadius: "var(--ys-r-pill)",
                      padding: "4px 12px",
                      border: isProminent
                        ? "1px solid var(--ys-orange-100)"
                        : "1px solid var(--ys-rule)",
                      background: isProminent
                        ? "var(--ys-primary-soft)"
                        : "transparent",
                      color: isProminent
                        ? "var(--ys-primary)"
                        : "var(--ys-ink-2)",
                    }}
                  >
                    {cat.category.title.ar}
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {/* Fallback to single category */}
        {(!store.categories || store.categories.length === 0) &&
          store.category?.title?.ar && (
            <div style={{ marginBottom: 12 }}>
              <Link
                to={`/category/${store.category.slug}`}
                style={{ textDecoration: "none" }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    fontFamily: "var(--ys-font)",
                    fontWeight: 500,
                    fontSize: 12,
                    borderRadius: "var(--ys-r-pill)",
                    padding: "4px 12px",
                    border: "1px solid var(--ys-orange-100)",
                    background: "var(--ys-primary-soft)",
                    color: "var(--ys-primary)",
                  }}
                >
                  {store.category.title.ar}
                </span>
              </Link>
            </div>
          )}

        {/* Tag chips */}
        {store.tags && store.tags.length > 0 && (
          <div className="hidden lg:flex flex-wrap gap-2">
            {/* Plain text on purpose: tag search was removed from /stores on 2026-07-19
                (quick-260719-fac), so a chip link only minted crawlable /stores?tags=
                duplicates that now return 410. */}
            {store.tags.map((tag, i) => (
              <span
                key={i}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontFamily: "var(--ys-font)",
                  fontWeight: 500,
                  fontSize: 12,
                  borderRadius: "var(--ys-r-pill)",
                  padding: "4px 12px",
                  border: "1px solid var(--ys-rule)",
                  background: "transparent",
                  color: "var(--ys-ink-3)",
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Right: Quick Details sidebar */}
      <QuickDetails store={store} />
    </div>
  );
}
